import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@app/auth/auth.service';
import { SocketService } from '@app/core/services/socket.service';
import { IMessage } from '@stomp/stompjs';

export interface NotificationCountResponseDto {
  userId: string;
  unreadCount: number;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavtabSocketService {
  private readonly NOTIFICATION_TOPIC = '/user/queue/notification-count';
  private readonly NOTIFICATION_REQUEST = '/app/get-unread-count';
  private readonly CHATS_TOPIC = '/user/queue/chat-count';
  private readonly CHATS_REQUEST = '/app/chat.getUnreadCount';

  private readonly socketService = inject(SocketService);
  private readonly authService = inject(AuthService);

  private _unreadCountNotificationsSignal = signal<number>(0);
  private _unreadChatsCountSignal = signal<number>(0);
  readonly unreadCountNotificationsSignal =
    this._unreadCountNotificationsSignal.asReadonly();
  readonly unreadChatsCountSignal = this._unreadChatsCountSignal.asReadonly();
  readonly tokenData = computed(() => {
    this.authService.token();
    return this.authService.getDecodedToken();
  });

  constructor() {
    this.setupSocketIntegration();
  }

  private setupSocketIntegration(): void {
    effect(() => {
      const isConnected = this.socketService.connected();
      if (!isConnected || !this.tokenData()) {
        return;
      }

      // Subscribirse al canal del contador de notificaciones
      this.socketService.subscribe(this.NOTIFICATION_TOPIC, (msg: IMessage) => {
        try {
          const body: NotificationCountResponseDto = JSON.parse(msg.body);
          this._unreadCountNotificationsSignal.set(body.unreadCount);
        } catch (error) {
          console.error(error);
        }
      });

      // Subscribirse al canal del contador de chats
      this.socketService.subscribe(this.CHATS_TOPIC, (msg: IMessage) => {
        try {
          const body: NotificationCountResponseDto = JSON.parse(msg.body);
          this._unreadChatsCountSignal.set(body.unreadCount);
        } catch (error) {
          console.error(error);
        }
      });

      // Pedir el contador inicial de notificaciones
      this.socketService.send(this.NOTIFICATION_REQUEST, {});

      // Pedir el contador inicial de chats
      this.socketService.send(this.CHATS_REQUEST, {});
    });
  }

  getUnreadNotificationsCount(): number {
    return this.unreadCountNotificationsSignal();
  }

  getUnreadChatsCount(): number {
    return this.unreadChatsCountSignal();
  }

  setUnreadChatsCountToZero() {
    this._unreadChatsCountSignal.set(0);
  }
}
